import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaLock, FaChartLine, FaDollarSign, FaRedo } from 'react-icons/fa';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  onEnded?: () => void;
  onProgress?: (progress: number) => void;
  isActive: boolean;
  isPaid?: boolean;
  isPromoted?: boolean;
}

export default function VideoPlayer({
  src,
  poster,
  onEnded,
  onProgress,
  isActive,
  isPaid,
  isPromoted
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [showReloadButton, setShowReloadButton] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset video when source changes
    video.pause();
    video.currentTime = 0;
    setIsPlaying(false);
    setShowPlayButton(true);
    setShowReloadButton(false);

    if (src.includes('.m3u8') && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
      });
      hls.loadSource(src);
      hls.attachMedia(video);

      return () => {
        hls.destroy();
      };
    } else {
      video.src = src;
    }
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const newProgress = (video.currentTime / video.duration) * 100;
      setProgress(newProgress);
      onProgress?.(newProgress);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setShowPlayButton(false);
      setShowReloadButton(true);
      onEnded?.();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setShowPlayButton(false);
      setShowReloadButton(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowPlayButton(true);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [onEnded, onProgress]);

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video || isPaid) return;

    try {
      if (video.paused) {
        await video.play();
        setIsPlaying(true);
        setShowPlayButton(false);
        setShowReloadButton(false);
      } else {
        video.pause();
        setIsPlaying(false);
        setShowPlayButton(true);
      }
    } catch (error) {
      console.error('Toggle play failed:', error);
      setIsPlaying(false);
      setShowPlayButton(true);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(!isMuted);
  };

  const handleReload = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.currentTime = 0;
    video.play().catch(() => {
      setShowPlayButton(true);
      setShowReloadButton(false);
    });
  };

  return (
    <motion.div 
      ref={containerRef}
      className="relative w-full h-full group"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="relative w-full" style={{ paddingBottom: '199.78%' }}> {/* 1080x1920 aspect ratio */}
        <video
          ref={videoRef}
          className="absolute top-0 left-0 w-full h-full object-cover"
          poster={poster}
          playsInline
          muted={isMuted}
          loop={false}
          onClick={togglePlay}
        />
      </div>

      {/* Status Badges */}
      <div className="absolute top-4 right-4 flex space-x-2">
        {isPromoted && (
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-3 py-1 rounded-full text-sm font-medium flex items-center">
            <FaChartLine className="w-4 h-4 mr-1" />
            Promoted
          </div>
        )}
        {isPaid && (
          <div className="bg-gradient-to-r from-pink-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
            <FaDollarSign className="w-4 h-4 mr-1" />
            Premium
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <motion.div 
        className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800 bg-opacity-50"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="h-full bg-red-500"
          style={{ width: `${progress}%` }}
          transition={{ duration: 0.1 }}
        />
      </motion.div>

      {/* Play/Pause/Reload Button (Center) */}
      <AnimatePresence>
        {(showPlayButton || showReloadButton) && (
          <motion.button
            onClick={showReloadButton ? handleReload : togglePlay}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-4 rounded-full bg-black bg-opacity-50 text-white"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {showReloadButton ? (
              <FaRedo className="w-8 h-8" />
            ) : isPlaying ? (
              <FaPause className="w-8 h-8" />
            ) : (
              <FaPlay className="w-8 h-8" />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Volume Button */}
      <motion.button
        onClick={toggleMute}
        className="absolute bottom-4 left-4 p-2 rounded-full bg-black bg-opacity-50 text-white opacity-0 group-hover:opacity-100"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        {isMuted ? (
          <FaVolumeMute className="w-6 h-6" />
        ) : (
          <FaVolumeUp className="w-6 h-6" />
        )}
      </motion.button>
    </motion.div>
  );
}